import React, { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../utils/cn'

const badgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-slate-900 text-slate-50 hover:bg-slate-900/80',
        secondary: 'bg-slate-100 text-slate-900 hover:bg-slate-100/80',
        destructive: 'bg-red-500 text-slate-50 hover:bg-red-500/80',
        outline: 'text-slate-950 border border-slate-200 bg-white',
        success: 'bg-green-100 text-green-800',
        warning: 'bg-yellow-100 text-yellow-800',
        info: 'bg-blue-100 text-blue-800',
        purple: 'bg-purple-100 text-purple-800',
        pink: 'bg-pink-100 text-pink-800',
        gray: 'bg-gray-100 text-gray-800',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
)

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean
  removable?: boolean
  onRemove?: () => void
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, dot, removable, onRemove, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props}>
        {dot ? <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" /> : null}
        {children}
        {removable ? (
          <button
            className="ml-1.5 inline-flex h-3 w-3 items-center justify-center rounded-full hover:bg-black/10 focus:outline-none"
            type="button"
            onClick={onRemove}
          >
            <span className="sr-only">Remove</span>
            <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 8 8">
              <path d="M1.5 1.5l5 5m-5 0l5-5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
            </svg>
          </button>
        ) : null}
      </div>
    )
  },
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants }
